"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, MessageSquare, Building2 } from "lucide-react";
import Image from "next/image";

const contactMethods = [
  {
    icon: Phone,
    title: "Sales Hotline",
    detail: "+91 1800-123-4567",
    note: "Mon-Sat, 9:00 AM to 8:00 PM IST",
  },
  {
    icon: Mail,
    title: "Business Email",
    detail: "sales@doexim.com",
    note: "Response target: within one business day",
  },
  {
    icon: MessageSquare,
    title: "Partnership Desk",
    detail: "partnerships@doexim.com",
    note: "Channel partners and enterprise onboarding",
  },
];

export default function ContactPage() {
  return (
    <div className="bg-white dark:bg-slate-950">

      {/* HERO WITH IMAGE */}
<section className="relative py-24 border-b text-center overflow-hidden">

  {/* BACKGROUND IMAGE */}
  <div className="absolute inset-0">
    <Image
      src="/contact-hero.jpg"
      alt="Contact Background"
      fill
      priority
      className="object-cover scale-105 opacity-80"
    />

    {/* OVERLAY */}
    <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white/70" />
  </div>

  {/* CONTENT */}
  <div className="relative container mx-auto px-4">
    <Badge className="bg-orange-100 text-orange-700">
      Contact
    </Badge>

    <h1 className="mt-4 text-4xl sm:text-5xl font-bold max-w-3xl mx-auto leading-tight">
      Talk to the{" "}
      <span className="text-orange-600 drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]">
        trade platform team
      </span>
    </h1>

    <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
      Get a guided demo, pricing consultation, or migration plan for your
      buyer and supplier workflows.
    </p>
  </div>

</section>

      {/* MAIN SECTION */}
      <section className="py-16">
        <div className="container mx-auto px-4 grid lg:grid-cols-3 gap-8">

          {/* FORM */}
          <div className="lg:col-span-2 border rounded-xl p-7 shadow-sm bg-white">
            <h2 className="text-2xl font-semibold mb-5">
              Request a Demo
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input id="firstName" placeholder="Aman" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input id="lastName" placeholder="Sharma" />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 mt-4">
              <div className="space-y-2">
                <Label htmlFor="companyEmail">Work Email</Label>
                <Input
                  id="companyEmail"
                  type="email"
                  placeholder="you@company.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" placeholder="+91 98xxxxxx21" />
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <Label htmlFor="company">Company</Label>
              <Input id="company" placeholder="Global Trade Pvt Ltd" />
            </div>

            <div className="space-y-2 mt-4">
              <Label htmlFor="message">
                What do you want to evaluate?
              </Label>
              <Textarea
                id="message"
                rows={5}
                placeholder="Tell us about your sourcing flow, RFQ volume, team size, and the modules you want to see."
              />
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              <Button>
                <Building2 className="mr-2 h-4 w-4" />
                Submit Demo Request
              </Button>

              <Button variant="outline" asChild>
                <Link href="/welcome/pricing">
                  View Pricing Plans
                </Link>
              </Button>
            </div>
          </div>

          {/* CONTACT CARDS */}
          <div className="space-y-4">
            {contactMethods.map((method) => (
              <div
                key={method.title}
                className="border rounded-xl p-5 shadow-sm bg-white"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                    <method.icon className="h-4 w-4" />
                  </div>

                  <h3 className="font-semibold">{method.title}</h3>
                </div>

                <p className="text-sm font-medium">{method.detail}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {method.note}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t text-center">
        <h2 className="text-xl font-semibold">
          Want to explore before talking?
        </h2>

        <p className="text-muted-foreground mt-2">
          Check pricing plans or platform features.
        </p>

        <div className="mt-6 flex justify-center gap-3">
          <Button asChild>
            <Link href="/welcome/pricing">
              View Plans
            </Link>
          </Button>

          <Button variant="outline" asChild>
            <Link href="/">
              Back to Home
            </Link>
          </Button>
        </div>
      </section>

    </div>
  );
}